import { supabase } from "@/integrations/supabase/client";

export interface EngineAnalysis {
  success?: boolean;
  duplicate?: boolean;
  manipulation_score?: number;
  toxicity_score?: number;
  urgency_score?: number;
  power_dynamic_score?: number;
  manipulation_primary_type?: string;
  secondary_pattern?: string;
  confidence_index?: string;
  signals_detected?: string[];
  subtext_decoder?: string;
  nuance?: string;
  strategy?: unknown;
  response?: string;
  reply_variants?: {
    soft_neutral: string;
    firm_boundary: string;
    minimal_grey_rock: string;
  } | null;
  viral_script?: string | null;
  hr_memo?: string | null;
  word_count?: number;
  error?: string;
}

/**
 * Calls the paid analyze-message edge function and parses the SSE stream.
 * The function charges 1 credit server-side (with a 60s duplicate guard).
 */
export async function runPaidAnalysis(
  message: string,
  mode: "professional" | "personal" | "public",
  relationshipType: string | null
): Promise<EngineAnalysis> {
  const session = (await supabase.auth.getSession()).data.session;
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const res = await fetch(`${supabaseUrl}/functions/v1/analyze-message`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: anonKey,
      Authorization: `Bearer ${session?.access_token}`,
    },
    body: JSON.stringify({ message, mode, relationship_type: relationshipType || null }),
  });

  const text = await res.text();

  if (!text.startsWith("data:")) {
    try {
      return JSON.parse(text);
    } catch {
      return { error: "Failed to parse response" };
    }
  }

  let lastComplete: EngineAnalysis | null = null;
  for (const line of text.split("\n")) {
    if (line.startsWith("data: ") && !line.includes("[DONE]")) {
      try {
        const payload = JSON.parse(line.slice(6));
        if (payload.type === "complete") lastComplete = payload;
      } catch { /* skip malformed events */ }
    }
  }

  return lastComplete ?? { error: "No complete event received" };
}
